import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { useEffect, useState } from "react";

const createCertificateField = (field = {}, index = 0) => ({
  id: field.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
  type: "certificate",
  name: field.name || "",
  icon_asset_id: field.icon_asset_id || "",
  doc_asset_id: field.doc_asset_id || "",
  icon_url: field.icon_url || "",
  doc_url: field.doc_url || "",
  icon_uploading: false,
  doc_uploading: false,
  order: field.order || index + 1,
  enabled: field.enabled ?? true,
});

export function EditBatch({ batch, onClose, currentUser }) {
  const [qrDetails, setQrDetails] = useState(null)
  const [errors, setErrors] = useState({})
  const [useCustomNote, setUseCustomNote] = useState(false)
  const [custom_note, setCustomNote] = useState('')
  const [enableCustomField, setEnableCustomField] = useState(false)
  const [customFields, setCustomFields] = useState([createCertificateField()])
  const [pendingDeleteAssetIds, setPendingDeleteAssetIds] = useState([])
  const [submitLoading, setSubmitLoading] = useState(false)
  const token = localStorage.getItem('token')

  const validateForm = () => {
    const newErrors = {}
    if (useCustomNote && !custom_note.trim()) {
      newErrors.custom_note = 'Note is required'
    }
    if (enableCustomField) {
      const hasInvalidField = customFields.some(
        (field) => !field.name.trim() || !field.icon_asset_id || !field.doc_asset_id
      )
      if (hasInvalidField) {
        newErrors.custom_fields = 'Each custom field needs a name, icon, and file upload'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const uploadAsset = async (file, usage) => {
    const uploadFormData = new FormData()
    uploadFormData.append('file', file)
    uploadFormData.append('usage', usage)

    const response = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/uploadassets`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: uploadFormData
    })

    const result = await response.json()
    if (!response.ok) {
      throw new Error(result?.message || 'Upload failed')
    }

    const assetId = result?.asset_id || result?.id || result?.data?.asset_id || result?.data?.id
    const assetUrl = result?.url || result?.data?.url || ''

    if (!assetId) {
      throw new Error('Upload succeeded but no asset id was returned')
    }

    return { assetId, assetUrl }
  }

  const cleanupAssets = async (assetIds = []) => {
    if (!assetIds.length) return
    await Promise.allSettled(
      assetIds.map((assetId) =>
        fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/assets/${assetId}?ifTemp=true`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      )
    )
  }

  const handleClose = async () => {
    if (submitLoading) return
    await cleanupAssets(pendingDeleteAssetIds)
    onClose()
  }

  const handleCustomFieldNameChange = (index, value) => {
    setCustomFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, name: value } : field))
    )

    if (errors.custom_fields) {
      setErrors((prev) => ({ ...prev, custom_fields: '' }))
    }
  }

  const handleAssetUpload = async (index, file, kind) => {
    if (!file) return

    const uploadingKey = kind === 'icon' ? 'icon_uploading' : 'doc_uploading'
    const idKey = kind === 'icon' ? 'icon_asset_id' : 'doc_asset_id'
    const urlKey = kind === 'icon' ? 'icon_url' : 'doc_url'

    setCustomFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, [uploadingKey]: true } : field))
    )

    try {
      const { assetId, assetUrl } = await uploadAsset(file, kind)
      setCustomFields((prev) =>
        prev.map((field, i) => {
          if (i !== index) return field

          const oldAssetId = field[idKey]
          if (oldAssetId && oldAssetId !== assetId) {
            setPendingDeleteAssetIds((ids) => [...new Set([...ids, oldAssetId])])
          }

          return { ...field, [idKey]: assetId, [urlKey]: assetUrl, [uploadingKey]: false }
        })
      )

      if (errors.custom_fields) {
        setErrors((prev) => ({ ...prev, custom_fields: '' }))
      }
    } catch (uploadError) {
      setCustomFields((prev) =>
        prev.map((field, i) => (i === index ? { ...field, [uploadingKey]: false } : field))
      )
      alert(uploadError.message || 'File upload failed')
    }
  }

  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { ...createCertificateField({}, prev.length), order: prev.length + 1 }])
  }

  const removeCustomField = (index) => {
    setCustomFields((prev) => {
      const field = prev[index]
      if (field?.icon_asset_id || field?.doc_asset_id) {
        setPendingDeleteAssetIds((ids) => [
          ...new Set([...ids, field.icon_asset_id, field.doc_asset_id].filter(Boolean))
        ])
      }

      const updated = prev.filter((_, i) => i !== index)
      return updated.length
        ? updated.map((item, idx) => ({ ...item, order: idx + 1 }))
        : [createCertificateField()]
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitLoading) return
    if (!validateForm()) {
      return
    }

    const filteredCustomFields = customFields
      .filter((f) => f.name.trim() && f.icon_asset_id && f.doc_asset_id)
      .map((field, index) => ({
        id: field.id,
        type: 'certificate',
        name: field.name.trim(),
        icon_asset_id: field.icon_asset_id,
        doc_asset_id: field.doc_asset_id,
        order: index + 1,
        enabled: true,
      }))

    try {
      setSubmitLoading(true)
      const res = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/updateqrcode/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          batch_id: batch.id,
          warning_limit: parseInt(qrDetails.warning_limit),
          scan_mobile_only: qrDetails.scan_mobile_only,
          ask_geo: qrDetails.ask_geo,
          ask_mobile: qrDetails.ask_mobile,
          pin: qrDetails.generate_pin, // this controls whether to generate pin
          redirect_url: qrDetails.redirect_url,
          youtube_url: qrDetails.youtube_url,
          linkedIn_url: qrDetails.linkedIn_url,
          meta_url: qrDetails.meta_url,
          instagram_url: qrDetails.instagram_url,
          x_url: qrDetails.x_url,
          show_first_scan: qrDetails.show_first_scan,
          show_ip: qrDetails.show_ip,
          useCustomNote,
          custom_note: useCustomNote ? custom_note.trim() : '',
          custom_fields: enableCustomField ? filteredCustomFields : []
        })
      })
      const data = await res.json();
      if (res.ok) {
        await cleanupAssets(pendingDeleteAssetIds)
        setPendingDeleteAssetIds([])
        alert("updated successfully")
        onClose()
      }

    } catch (error) {
      console.error(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  useEffect(() => {
    const fetchQrCodeStatus = async () => {
      const result = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/firstqrcode/${batch.id}`);
      const data = await result.json();
      if (data.pin === null) {
        data.generate_pin = false;
      } else {
        data.generate_pin = true;
      }

      const existingCustomFields = data.custom_fields || data.batches?.custom_fields || []
      const existingUseCustomNote = Boolean(
        data.batches?.useCustomNote
        ?? data.useCustomNote
        ?? data.batches?.use_custom_note
        ?? data.use_custom_note
      )
      const existingCustomNote = data.batches?.custom_note || data.custom_note || ''

      setQrDetails({
        ...data,
        redirect_url: data.batches?.redirect_url || "",
        youtube_url: data.batches?.youtube_url || "",
        meta_url: data.batches?.meta_url || "",
        instagram_url: data.batches?.instagram_url || "",
        linkedIn_url: data.batches?.linkedIn_url || "",
        x_url: data.batches?.x_url || "",
        show_ip: data.batches?.show_ip || false,
        show_first_scan: data.batches?.show_first_scan || false
      });

      setUseCustomNote(existingUseCustomNote)
      setCustomNote(existingCustomNote)

      setEnableCustomField(existingCustomFields.length > 0)
      setCustomFields(
        existingCustomFields.length
          ? existingCustomFields.map((field, index) => createCertificateField(field, index))
          : [createCertificateField()]
      )
    };
    fetchQrCodeStatus();
  }, [batch.id]);

  if (!qrDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-10 text-center text-gray-700">
          Loading QR details...
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-text-primary">{batch.title}</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="#6b7280" />
          </button>
        </div>
        <div className="space-y-4">

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="overflow-y-auto px-6 py-2 max-h-[62vh] space-y-6">
              {/* batch data */}
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                <input name="batch_code" hidden readOnly value={batch.batch_code} />

                {/* Batch Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batch Name *
                  </label>
                  <Input
                    type="text"
                    name="title"
                    value={batch.title}
                    disabled
                    readOnly
                  />
                </div>

                {/* Warning Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warning Limit
                  </label>
                  <Input
                    type="number"
                    name="warning_limit"
                    value={qrDetails.warning_limit || ""}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, warning_limit: parseInt(e.target.value) || 0 }))
                    }
                    placeholder="Enter warning limit"
                  />
                </div>


              </div>

              {/* url */}
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2">

                {/* Redirect_url */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Redirect Url
                  </label>
                  <Input
                    type='url'
                    name="redirect_url"
                    value={qrDetails.redirect_url || ""}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, redirect_url: e.target.value }))
                    }
                    placeholder="Enter url to redirect user"
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1' >
                    Youtube Channel Url
                  </label>
                  <Input
                    type="url"
                    name="youtube_url"
                    value={qrDetails.youtube_url}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, youtube_url: e.target.value }))
                    }
                    placeholder="Enter YouTube url"
                  />
                </div>

              </div>

              {/* social url */}
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                {/* meta url */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1' >
                    Facebook Url
                  </label>
                  <Input
                    type="url"
                    name="meta_url"
                    value={qrDetails.meta_url || ""}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, meta_url: e.target.value }))
                    }
                    placeholder="Enter facebook url"
                  />
                </div>
                {/* Instagram url */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1' >
                    Instagram Url
                  </label>
                  <Input
                    type="url"
                    name="instagram_url"
                    value={qrDetails.instagram_url}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, instagram_url: e.target.value }))
                    }
                    placeholder="Enter Instagram url"
                  />
                </div>
              </div>

              {/* social url */}
              <div className="gap-6 grid grid-cols-1 md:grid-cols-2">
                {/* LinkedIn url  */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1' >
                    LinkedIn Url
                  </label>
                  <Input
                    type="url"
                    name="linkedIn_url"
                    value={qrDetails.linkedIn_url || ""}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, linkedIn_url: e.target.value }))
                    }
                    placeholder="Enter LinkedIn url"
                  />
                </div>

                {/* X (formerly Twitter) Url */}
                <div>

                  <label className='block text-sm font-medium text-gray-700 mb-1' >
                    X (formerly Twitter) Url
                  </label>
                  <Input
                    type="url"
                    name="x_url"
                    value={qrDetails.x_url}
                    onChange={(e) =>
                      setQrDetails(prev => ({ ...prev, x_url: e.target.value }))
                    }
                    placeholder="Enter x url"
                  />
                </div>
              </div>


              {/* Options */}
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                {/* Generate PIN */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="generate_pin"
                    name="generate_pin"
                    checked={qrDetails.generate_pin}
                    className="rounded-full"
                    onChange={(e) =>
                      setQrDetails((prev) => ({ ...prev, generate_pin: e.target.checked }))
                    }
                  />
                  <label htmlFor="generate_pin" className="text-sm font-medium text-gray-700">
                    Generate 4-digit PIN
                  </label>
                </div>

                {/* Ask Mobile */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="ask_mobile"
                    name="ask_mobile"
                    checked={qrDetails.ask_mobile}
                    className="rounded-full"
                    onChange={(e) =>
                      setQrDetails((prev) => ({ ...prev, ask_mobile: e.target.checked }))
                    }
                  />
                  <label htmlFor="ask_mobile" className="text-sm font-medium text-gray-700">
                    Ask for Mobile Number on Scan
                  </label>
                </div>

                {/* Scan Mobile Only */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="scan_mobile_only"
                    name="scan_mobile_only"
                    checked={qrDetails.scan_mobile_only}
                    className="rounded-full"
                    onChange={(e) =>
                      setQrDetails((prev) => ({ ...prev, scan_mobile_only: e.target.checked }))
                    }
                  />
                  <label htmlFor="scan_mobile_only" className="text-sm font-medium text-gray-700">
                    Allow Scan from Mobile Devices Only
                  </label>
                </div>

                {/* Ask Geolocation */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="ask_geo"
                    name="ask_geo"
                    checked={qrDetails.ask_geo}
                    className="rounded-full"
                    onChange={(e) =>
                      setQrDetails((prev) => ({ ...prev, ask_geo: e.target.checked }))
                    }
                  />
                  <label htmlFor="ask_geo" className="text-sm font-medium text-gray-700">
                    Ask for Geolocation
                  </label>
                </div>

                {/* Ask show_ip */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="show_ip"
                    name="show_ip"
                    checked={qrDetails.show_ip}
                    className="rounded-full"
                    onChange={(e) =>
                      setQrDetails((prev) => ({ ...prev, show_ip: e.target.checked }))
                    }
                  />
                  <label htmlFor="show_ip" className="text-sm font-medium text-gray-700">
                    Show User IP Address
                  </label>
                </div>

                {/* Ask show_first_scan */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="show_first_scan"
                    name="show_first_scan"
                    checked={qrDetails.show_first_scan}
                    className="rounded-full"
                    onChange={(e) =>
                      setQrDetails((prev) => ({ ...prev, show_first_scan: e.target.checked }))
                    }
                  />
                  <label htmlFor="show_first_scan" className="text-sm font-medium text-gray-700">
                    Show first scan
                  </label>
                </div>

              </div>

              {/* Use Custom Note */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="use_custom_note"
                    className="text-sm font-medium text-gray-700"
                  >
                    Use Custom Note
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      setUseCustomNote((prev) => {
                        const next = !prev
                        if (!next) {
                          setCustomNote('')
                          setErrors((prevErrors) => ({ ...prevErrors, custom_note: '' }))
                        }
                        return next
                      })
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${useCustomNote ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useCustomNote ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>

                {useCustomNote && (
                  <div>
                    <label htmlFor="custom_note" className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Notes
                    </label>
                    <Input
                      type="text"
                      name="custom_note"
                      value={custom_note}
                      className={errors.custom_note ? 'border-red-500' : ''}
                      onChange={(e) => {
                        setCustomNote(e.target.value)
                        if (errors.custom_note) {
                          setErrors((prev) => ({ ...prev, custom_note: '' }))
                        }
                      }}
                    />
                    {errors.custom_note && (
                      <span className="flex items-center gap-2 mt-1 text-sm text-red-600">
                        <Icon name="AlertCircle" size={14} /> {errors.custom_note}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="enable_custom_field"
                    className="text-sm font-medium text-gray-700"
                  >
                    Enable Custom Field
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      setEnableCustomField(!enableCustomField)
                      if (enableCustomField) {
                        setCustomFields((prev) => {
                          const idsToDelete = prev
                            .flatMap((field) => [field.icon_asset_id, field.doc_asset_id])
                            .filter(Boolean)
                          if (idsToDelete.length) {
                            setPendingDeleteAssetIds((ids) => [...new Set([...ids, ...idsToDelete])])
                          }
                          return [createCertificateField()]
                        })
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enableCustomField ? "bg-blue-600" : "bg-gray-300"}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableCustomField ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </button>
                </div>

                {errors.custom_fields && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <Icon name="AlertCircle" size={14} /> {errors.custom_fields}
                  </p>
                )}
              </div>

              {enableCustomField && (
                <div className="mt-4 space-y-4">
                  {customFields.map((field, index) => (
                    <div key={field.id || index} className="relative p-4 rounded-xl border border-gray-200 bg-white shadow-sm space-y-4 transition-all hover:border-blue-200">
                      <div className="flex justify-between items-center border-b border-gray-50 pb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                          Field #{index + 1}
                        </span>
                        {customFields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCustomField(index)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
                        <Input
                          type="text"
                          value={field.name || ''}
                          onChange={(e) => handleCustomFieldNameChange(index, e.target.value)}
                          placeholder="e.g. Download Certificate"
                          className="bg-gray-50/50"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-600">Icon Image</label>
                          <div className={`relative flex items-center justify-center border-2 border-dashed rounded-lg p-2 transition-colors ${field.icon_asset_id ? 'border-green-200 bg-green-50/30' : 'border-gray-200 hover:border-blue-300'}`}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleAssetUpload(index, e.target.files?.[0], 'icon')}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <div className="text-center">
                              {field.icon_uploading ? (
                                <span className="text-[10px] text-blue-500 animate-pulse">Uploading...</span>
                              ) : field.icon_asset_id ? (
                                <span className="text-[10px] text-green-600 flex items-center gap-1"><Icon name="Check" size={12} /> Icon Ready</span>
                              ) : (
                                <span className="text-[10px] text-gray-500">Click to upload icon</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-600">Attachment (PDF/DOC)</label>
                          <div className={`relative flex items-center justify-center border-2 border-dashed rounded-lg p-2 transition-colors ${field.doc_asset_id ? 'border-green-200 bg-green-50/30' : 'border-gray-200 hover:border-blue-300'}`}>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,image/*"
                              onChange={(e) => handleAssetUpload(index, e.target.files?.[0], 'doc')}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <div className="text-center">
                              {field.doc_uploading ? (
                                <span className="text-[10px] text-blue-500 animate-pulse">Uploading...</span>
                              ) : field.doc_asset_id ? (
                                <span className="text-[10px] text-green-600 flex items-center gap-1"><Icon name="Check" size={12} /> File Ready</span>
                              ) : (
                                <span className="text-[10px] text-gray-500">Click to upload file</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addCustomField}
                    className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-200 transition-all"
                  >
                    <Icon name="Plus" size={16} /> Add Another Attachment
                  </button>
                </div>
              )}

            </div>
            {/* Actions */}
            <div className="flex justify-end space-x-3 px-6 py-2 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleClose} disabled={submitLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="text-white" loading={submitLoading}>
                Update Batch
              </Button>
            </div>

          </form>
        </div>
      </div>
    </div >
  )
}
